import { useState, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { PicklistMapping } from '@/types/field-mapping-flow';

interface PicklistOption {
  value: string;
  label: string;
}

interface PicklistMappingDialogProps {
  open: boolean;
  onClose: () => void;
  fieldName: string;
  fieldLabel: string;
  picklistOptions: PicklistOption[];
  existingMapping: PicklistMapping[];
  onSave: (mapping: PicklistMapping[]) => void;
  saasCategory?: string;
}

export function PicklistMappingDialog({
  open,
  onClose,
  fieldName,
  fieldLabel,
  picklistOptions,
  existingMapping,
  onSave,
  saasCategory = 'CRM',
}: PicklistMappingDialogProps) {
  // State: one row per CRM picklist option with its mapped event values
  const [mappingRows, setMappingRows] = useState<
    Array<{ saas_value: string; saas_label: string; tpa_values: string[] }>
  >([]);
  const [addingValueFor, setAddingValueFor] = useState<string | null>(null);
  const [newTagValue, setNewTagValue] = useState('');

  // Initialize rows from picklist options + existing mapping
  useEffect(() => {
    if (!open) return;

    const rows = picklistOptions.map((opt) => {
      const existingRow = existingMapping.find(
        (m) => m.saas_value === opt.value
      );
      return {
        saas_value: opt.value,
        saas_label: opt.label,
        tpa_values: existingRow?.tpa_values || [],
      };
    });

    setMappingRows(rows);
    setAddingValueFor(null);
    setNewTagValue('');
  }, [open, picklistOptions, existingMapping]);

  const handleAddTag = (saasValue: string) => {
    if (!newTagValue.trim()) return;

    setMappingRows((prev) =>
      prev.map((row) =>
        row.saas_value === saasValue
          ? { ...row, tpa_values: [...row.tpa_values, newTagValue.trim()] }
          : row
      )
    );
    setNewTagValue('');
    setAddingValueFor(null);
  };

  const handleRemoveTag = (saasValue: string, tagIndex: number) => {
    setMappingRows((prev) =>
      prev.map((row) =>
        row.saas_value === saasValue
          ? {
              ...row,
              tpa_values: row.tpa_values.filter((_, i) => i !== tagIndex),
            }
          : row
      )
    );
  };

  const handleSave = () => {
    // Only include rows that have at least one mapped event value
    const mapping: PicklistMapping[] = mappingRows
      .filter((row) => row.tpa_values.length > 0)
      .map((row) => ({
        saas_value: row.saas_value,
        tpa_values: row.tpa_values,
      }));

    onSave(mapping);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="mx-4 flex max-h-[80vh] w-full max-w-lg flex-col rounded-xl border border-border bg-background shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              Picklist Mapping
            </h3>
            <p className="text-[11px] text-muted-foreground">
              {fieldLabel} ({fieldName})
            </p>
          </div>
          <Button variant="ghost" size="icon-sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Description */}
        <div className="border-b border-border bg-muted/30 px-4 py-2">
          <p className="text-[11px] text-muted-foreground">
            Map {saasCategory} picklist values to possible incoming event values. When an
            event value matches any tag, the corresponding {saasCategory} value will be used.
          </p>
        </div>

        {/* Mapping rows */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {mappingRows.map((row) => (
            <div
              key={row.saas_value}
              className="rounded-lg border border-border p-3"
            >
              {/* CRM value header */}
              <div className="mb-2 flex items-center gap-2">
                <Badge variant="outline" className="text-[10px]">
                  {fieldLabel}
                </Badge>
                <span className="text-xs font-medium text-foreground">
                  {row.saas_label || row.saas_value}
                </span>
              </div>

              {/* Event value tags */}
              <div className="flex flex-wrap items-center gap-1">
                {row.tpa_values.map((tag, tagIndex) => (
                  <Badge
                    key={`${tag}-${tagIndex}`}
                    variant="secondary"
                    className="gap-1 text-[10px]"
                  >
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(row.saas_value, tagIndex)}
                      className="ml-0.5 rounded-full hover:bg-destructive/20"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </Badge>
                ))}

                {addingValueFor === row.saas_value ? (
                  <input
                    type="text"
                    value={newTagValue}
                    onChange={(e) => setNewTagValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddTag(row.saas_value);
                      if (e.key === 'Escape') {
                        setAddingValueFor(null);
                        setNewTagValue('');
                      }
                    }}
                    onBlur={() => {
                      if (newTagValue.trim()) {
                        handleAddTag(row.saas_value);
                      } else {
                        setAddingValueFor(null);
                      }
                    }}
                    placeholder="Event value..."
                    className="h-6 w-24 rounded border border-border bg-background px-1.5 text-[10px] text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    autoFocus
                  />
                ) : (
                  <button
                    onClick={() => {
                      setAddingValueFor(row.saas_value);
                      setNewTagValue('');
                    }}
                    className="flex h-6 items-center gap-0.5 rounded border border-dashed border-border px-1.5 text-[10px] text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                  >
                    <Plus className="h-2.5 w-2.5" />
                    Add
                  </button>
                )}
              </div>
            </div>
          ))}

          {mappingRows.length === 0 && (
            <p className="py-4 text-center text-xs text-muted-foreground">
              No picklist options available for this field
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave}>
            Save Mapping
          </Button>
        </div>
      </div>
    </div>
  );
}
